import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Single source of truth for the dark navbar surface.
 * Used by both the marketing header and the authenticated app shell
 * so colors, blur, borders, focus and disabled states never drift.
 *
 * The `nav-dark` utility (src/styles.css) provides:
 *  - solid dark background + blur (consistent across scroll & routes)
 *  - readable text/icon defaults in hover / focus-visible / disabled
 *  - color-scheme: dark so native controls inherit correctly
 *
 * The `data-testid="site-navbar"` + `data-theme="dark"` hooks are required
 * by NavbarDarkGuard and the e2e tests.
 */
export const DarkNavSurface = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { as?: "header" | "div" | "aside" }
>(function DarkNavSurface({ as = "header", className, children, ...rest }, ref) {
  const Comp = as as any;
  return (
    <Comp
      ref={ref}
      data-theme="dark"
      data-testid="site-navbar"
      className={cn("nav-dark border-b", className)}
      {...rest}
    >
      {children}
    </Comp>
  );
});

export const DarkNavPanel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function DarkNavPanel({ className, children, ...rest }, ref) {
  return (
    <div
      ref={ref}
      data-theme="dark"
      data-testid="site-navbar-panel"
      className={cn("nav-dark-panel border", className)}
      {...rest}
    >
      {children}
    </div>
  );
});
